import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Modal from '~/Common-Objects/Modal/index'
import i18n from '~/i18n'
import axios from '~/data/http/axios'
import AddDomain from './addDomain'
import AuthenticateIntro from './authentication/intro'
import SendInstructions from './authentication/sendInstructions'
import FlowTemplate from './authentication/flow/flowTemplate'

export default function DomainManagement({
    finish = () => {},
    forDomainId = null,
    step = 'addDomain',
    addNewDomainInitialEmailAddress = null
}) {
    const site = useSelector((state) => state.siteMaster)

    const [loading, setLoading] = useState(true)
    const [flowStep, setFlowStep] = useState(step)
    const [error, setError] = useState('')
    const [domain, setDomain] = useState(null)
    const [domainProviders, setDomainProviders] = useState([])
    const [domainProvider, setDomainProvider] = useState(null)
    const [domainRecords, setDomainRecords] = useState()

    const [showBackButton, setShowBackButton] = useState(false)
    const [backButtonAction, setBackButtonAction] = useState(() => {})

    useEffect(() => {
        let domainRecordsTmp = [
            {
                type: 'SPFDKIM',
                recordType: 'CNAME',
                host: 'mp1._domainkey',
                value: 'dkim.ourmailsender.com',
                enabled: false,
                required: true,
                exists: false,
                complete: false,
                id: 1
            },
            {
                type: 'SPFDKIM',
                recordType: 'TXT',
                host: '@',
                value: `v=spf1 include:spf.ourmailsender.com${site?.spfRecordUrl ? ` include:${site?.spfRecordUrl}.ourmailsender.com` : ''} -all`,
                enabled: false,
                required: true,
                exists: false,
                complete: false,
                id: 2
            },
            {
                type: 'DMARC',
                recordType: 'TXT',
                host: '_dmarc',
                value: 'v=DMARC1; p=none; sp=none; adkim=r; aspf=r',
                enabled: false,
                required: false,
                exists: false,
                complete: false,
                id: 3
            },
            {
                type: 'TRACKING',
                recordType: 'CNAME',
                host: 'mptrack',
                value: 'api.ourmailsender.com',
                enabled: false,
                required: false,
                id: 3
            },
            {
                type: 'CDN',
                recordType: 'CNAME',
                host: 'mpcdn',
                value: 'cdn1.ourmailsender.com',
                enabled: false,
                required: false,
                id: 4
            }
        ]
        setDomainRecords(domainRecordsTmp)
        init()
    }, [])

    const init = async () => {
        const propsStep = step
        let domainProviders = []
        try {
            const response = await axios.get(`/accountMaster/domain/providers`)
            domainProviders = response.data.providers
        } catch (err) {
            setError('Error loading domain - please try again later')
            setLoading(false)
            return
        }

        let finalProviders = domainProviders
        finalProviders.unshift({
            label: "My provider is not listed / I'm not sure",
            value: 'NONE',
            icon: 'questionCircleOutline',
            iconColor: '#7C8086',
            customRight: (
                <div
                    className='text-sml text-grey-800'
                    style={{
                        flex: 0,
                        minWidth: '150px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                    }}
                >
                    <div className='mar-r5'>Manual Setup</div>
                    <Icons icon='arrowRight' color='#7C8086' style={{ height: 20 }} />
                </div>
            ),
            instructions: [
                `<strong>Log in to your DNS provider’s account</strong> - This is typically your domain registrar or DNS host. If you’re not sure, you can use the DNS lookup tool <a href="https://who.is" target="_blank">who.is</a> to look up your domain.`,
                '<strong>Find your domain’s DNS settings</strong> - This may be under headings like DNS Settings, Zone Editor, or Advanced DNS.'
            ]
        })

        setDomainProviders(finalProviders)

        let tmpFlowStep = 'addDomain'
        if (forDomainId) {
            const foundDomainResult = await getDomain(forDomainId)
            if (foundDomainResult.success) {
                const foundDomain = foundDomainResult.domain
                if (foundDomain.serviceProvider) {
                    const foundProv = finalProviders.find((provider) => provider.value == foundDomain.serviceProvider)
                    if (foundProv) {
                        setDomainProvider(foundProv)
                    } else {
                        setDomainProvider(finalProviders[0])
                    }
                } else {
                    setDomainProvider(finalProviders[0])
                }

                if (!foundDomain.verified) {
                    tmpFlowStep = 'addDomainVerify'
                } else if (!foundDomain?.options?.doneDnsIntro) {
                    tmpFlowStep = 'authenticateDomain'
                } else if (!foundDomain.authenticated && !foundDomain.serviceProvider) {
                    tmpFlowStep = 'domainServicePicker'
                } else if (
                    (!foundDomain.authenticated && foundDomain.serviceProvider) ||
                    !foundDomain.dmarc ||
                    !foundDomain.trackingDomain?.authenticated ||
                    !foundDomain.cdnDomain?.authenticated
                ) {
                    tmpFlowStep = 'dnsInstructions'
                }

                if (
                    (foundDomain?.options?.doneDnsInstructions && propsStep != 'dnsInstructions') ||
                    foundDomain?.options?.forwardedInstructions
                ) {
                    tmpFlowStep = 'verifySetup'
                }

                //if everythings done, sent to verify setup
                if (
                    foundDomain?.authenticated &&
                    foundDomain?.dmarc &&
                    foundDomain?.trackingDomain?.authenticated &&
                    foundDomain?.cdnDomain?.authenticated
                ) {
                    tmpFlowStep = 'verifySetup'
                }
            } else {
                setError('Domain not found')
                setLoading(false)
            }
        }
        goSetFlowStep(tmpFlowStep)
        setLoading(false)
    }

    const getDomain = async (ourDomainId) => {
        try {
            if (!ourDomainId && domain?.id) {
                ourDomainId = domain.id
            }
            const response = await axios.get(`/accountMaster/domain/${ourDomainId}`)
            setDomain(response.data)

            return {
                success: true,
                domain: response.data
            }
        } catch (err) {
            setError('Error loading domain - please try again later')
            return { success: false }
        }
    }

    const addedDomain = async (newDomainId) => {
        await getDomain(newDomainId)
        goSetFlowStep('authenticateDomain')
    }

    const updateDomain = async (updates) => {
        await axios.put(`/accountMaster/domain/${domain.id}`, updates)
        await getDomain(domain.id)
    }

    const goSetFlowStep = (step) => {
        setBackButtonAction('')
        setShowBackButton(false)
        if (step == 'sendInstructions') {
            if (domain?.options?.doneDnsIntro) {
                setBackButtonAction('verifySetup')
            } else {
                setBackButtonAction('authenticateDomain')
            }
            setShowBackButton(true)
        }
        if (step == 'domainServicePicker') {
            setBackButtonAction('authenticateDomain')
            setShowBackButton(true)
        }
        if (
            step == 'dnsInstructions' ||
            step == 'spfDkimSettings' ||
            step == 'dmarcSettings' ||
            step == 'trackingSettings' ||
            step == 'cdnSettings'
        ) {
            if (domain?.options?.doneDnsIntro) {
                setBackButtonAction('verifySetup')
            } else {
                setBackButtonAction('domainServicePicker')
            }
            setShowBackButton(true)
        }

        setFlowStep(step)
    }

    const backButtonGo = () => {
        goSetFlowStep(backButtonAction)
    }

    if (loading || !flowStep) {
        return (
            <Modal
                width='90%'
                height='90%'
                noClickAway={true}
                blurBg={true}
                top={false}
                closeButton={true}
                onCloseButton={() => {
                    if (error) {
                        finish()
                    }
                }}
                noInnerPaperBorder={true}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}
                >
                    {error && <div className='mar-b15 text-center text-red text-md'>{error}</div>}
                    {!error && (
                        <>
                            <div className='mar-b10'>
                                <LoadingIcons iconType='2' style={{ top: 0 }} />
                            </div>
                            <div className='text-xs text-grey-800'>
                                {i18n.t('company:domains.domainManagement.loadingDomainDetails')}
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        )
    }

    const activeComponent = () => {
        if (error) {
            return (
                <div>
                    <h2 className='mar-b25 text-center'>There was an error</h2>
                    <div className='mar-b15 text-center text-grey-800 text-md'>The following error occurred:</div>
                    <div className='mar-b15 text-center text-red text-md'>{error}</div>
                    <div className='text-center text-grey-800 text-md'>If this persists, please contact support.</div>
                </div>
            )
        }
        if (flowStep == 'addDomain')
            return <AddDomain onSuccess={addedDomain} initialEmailAddress={addNewDomainInitialEmailAddress} />
        if (flowStep == 'addDomainVerify')
            return (
                <AddDomain
                    onSuccess={addedDomain}
                    isVerifying={true}
                    originalEmailAddress={domain.originalEmailAddress}
                />
            )
        //Authentication
        if (flowStep == 'authenticateDomain') {
            return <AuthenticateIntro setFlowStep={goSetFlowStep} domain={domain} updateDomain={updateDomain} />
        } else if (flowStep == 'sendInstructions') {
            return <SendInstructions domain={domain} setFlowStep={goSetFlowStep} updateDomain={updateDomain} />
        } else {
            return (
                <FlowTemplate
                    getDomain={getDomain}
                    finish={finish}
                    domainProviders={domainProviders}
                    domainProvider={domainProvider}
                    setDomainProvider={setDomainProvider}
                    domain={domain}
                    currentStep={flowStep}
                    setFlowStep={goSetFlowStep}
                    domainRecords={domainRecords}
                    setDomainRecords={setDomainRecords}
                    updateDomain={updateDomain}
                />
            )
        }

        return <div></div>
    }

    return (
        <Modal
            width='90%'
            height='90%'
            noClickAway={true}
            blurBg={true}
            top={false}
            closeButton={true}
            onCloseButton={finish}
            noInnerPaperBorder={true}
            backButton={showBackButton}
            backButtonAction={backButtonGo}
        >
            <div style={{ position: 'absolute', bottom: 20, right: 20, zIndex: 1000 }}>
                <Hint
                    style={{ cursor: 'pointer', margin: 0 }}
                    className='posthog-survey-domain-management'
                    iconLeft={<Icons icon='questionCircle' color='#2B2F41' style={{ height: 16, width: 16 }} />}
                >
                    Give Feedback
                </Hint>
            </div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', height: '100%' }}>
                <div className='newUserFormOuterContentHolder'>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                        className='mar-b25'
                    >
                        {activeComponent()}
                    </div>
                </div>
            </div>
        </Modal>
    )
}
